import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Settings, ChevronRight, Circle, Monitor, Layers, LayoutGrid, ArrowRight,
  Server, Users, Briefcase, FileJson, BarChart3, Globe, FileText, Menu, X,
  Database, Shield, Calendar, Clock, Upload, Download, Building2, MapPin,
  Printer, Barcode, Receipt, CreditCard, Truck, UserCog, Key, Activity, Search,
  Home, DollarSign, ShoppingCart, Package, Store, Warehouse, Cog, Hash, RefreshCw, Loader2,
  ToggleLeft, ToggleRight, ChevronDown, Zap
} from 'lucide-react';
import api from '@/services/api';
import { ModuleId } from '@/types';
import { useLicense } from '@/context/LicenseContext';
import { useERPContextSafe } from '@/context/ERPContext';
import { TenantHubTab } from './TenantHubTab';

// Dynamic icon resolver — matches module name keywords to icons
const getModuleIconByName = (name, size = 18) => {
  if (!name) return <Hash size={size} />;
  const n = name.toLowerCase();
  if (n.includes('dash')) return <Home size={size} />;
  if (n.includes('financ') || n.includes('account')) return <DollarSign size={size} />;
  if (n.includes('logist') || n.includes('transport')) return <Truck size={size} />;
  if (n.includes('procure') || n.includes('purchase')) return <ShoppingCart size={size} />;
  if (n.includes('inventory') || n.includes('stock')) return <Package size={size} />;
  if (n.includes('pos') || n.includes('billing') || n.includes('store')) return <Store size={size} />;
  if (n.includes('payroll') || n.includes('salary') || n.includes('hr')) return <Users size={size} />;
  if (n.includes('warehouse')) return <Warehouse size={size} />;
  if (n.includes('setup') || n.includes('setting') || n.includes('config')) return <Cog size={size} />;
  if (n.includes('system') || n.includes('manager')) return <Settings size={size} />;
  if (n.includes('data') || n.includes('database')) return <Database size={size} />;
  if (n.includes('report')) return <BarChart3 size={size} />;
  if (n.includes('web') || n.includes('server')) return <Globe size={size} />;
  if (n.includes('roll') || n.includes('role')) return <Briefcase size={size} />;
  if (n.includes('replen') || n.includes('replan')) return <RefreshCw size={size} />;
  if (n.includes('payment') || n.includes('credit')) return <CreditCard size={size} />;
  if (n.includes('license') || n.includes('about')) return <FileText size={size} />;
  if (n.includes('button') || n.includes('active') || n.includes('menu')) return <Layers size={size} />;
  return <Hash size={size} />;
};

// Map module IDs to ModuleId constants for navigation
const MODULE_ID_TO_LAUNCH = {
  1: ModuleId.DASHBOARD,
  2: 'FINANCE',
  3: ModuleId.LOGISTICS,
  4: 'PROCUREMENT',
  5: 'INVENTORY',
  6: 'POS',
  7: ModuleId.PAYROLL,
  8: 'PRODUCTION',
};

// Get module category based on ID
const getModuleCategory = (moduleId, moduleName) => {
  const name = (moduleName || '').toLowerCase();
  if (moduleId === 1) return 'General';
  if (moduleId === 2) return 'Finance';
  if ([3, 4, 5, 8].includes(moduleId)) return 'Operations';
  if (moduleId === 6) return 'Sales';
  if (moduleId === 7) return 'HR';
  if (moduleId >= 20 && moduleId <= 27) return 'Setup';
  if (name.includes('admin') || name.includes('system')) return 'Admin';
  if (name.includes('tool')) return 'Tools';
  return 'General';
};

// Setup module IDs (20-27) - these are shown ONLY in Setup Center, not in Apps tab
const SETUP_MODULE_IDS = [20, 21, 22, 23, 24, 25, 26, 27];

/* ═══════════════════════════════════════════════════════════════════════════
   COMPLETE SETUP MODULES - 100% COVERAGE FROM MYACCOUNTS ARCHITECTURE
   All modules, submodules, and screens mapped exactly as per source document
═══════════════════════════════════════════════════════════════════════════ */
const SETUP_MODULES = [
  {
    id: 'MYACCOUNTS_SETUP', 
    label: 'MyAccounts Setup', 
    icon: <Server size={16} />,
    description: 'Core server, warehouse, and client configuration',
    sections: [
      { 
        label: 'Systems Server Setup', 
        items: [
          { id: 'mas-srv', label: 'Server', moduleId: 'MYACCOUNTS_SETUP' },
          { id: 'mas-wh', label: 'Warehouse', moduleId: 'MYACCOUNTS_SETUP' },
          { id: 'mas-br', label: 'Branches', moduleId: 'MYACCOUNTS_SETUP' },
        ]
      },
      { 
        label: 'General', 
        items: [
          { id: 'mas-cli', label: 'Client Information', moduleId: 'MYACCOUNTS_SETUP' }
        ]
      }
    ]
  },
  {
    id: 'SYSTEM_MANAGER', 
    label: 'System Manager', 
    icon: <Settings size={16} />,
    description: 'User management, modules, screens, and system audits',
    sections: [
      { 
        label: 'USER UAMS', 
        items: [
          { id: 'USER_GROUPS', label: 'User Groups', moduleId: 'USER_GROUPS' },
          { id: 'USER_ROLES', label: 'User Roles', moduleId: 'USER_ROLES' },
          { id: 'USER_LEVELS', label: 'User Levels', moduleId: 'USER_LEVELS' },
          { id: 'USER_CREATION', label: 'User Creation', moduleId: 'USER_CREATION' },
          { id: 'USER_PRIVILEGES', label: 'User Privilege (User Profile)', moduleId: 'USER_PRIVILEGES' },
          { id: 'sm-uams-ctrl', label: 'Control Management', moduleId: 'SYSTEM_MANAGER' },
          { id: 'sm-uams-trc', label: 'Event Trace Report', moduleId: 'SYSTEM_MANAGER' },
        ]
      },
      { 
        label: 'Setup', 
        items: [
          { id: 'MODULE_MASTER', label: 'Modules', moduleId: 'MODULE_MASTER' },
          { id: 'SUBMODULE_MASTER', label: 'Sub Modules', moduleId: 'SUBMODULE_MASTER' },
          { id: 'SCREEN_MASTER', label: 'User Screens', moduleId: 'SCREEN_MASTER' },
          { id: 'sm-set-vert', label: 'Vertical Master Setup', moduleId: 'SYSTEM_MANAGER' },
          { id: 'sm-set-tb', label: 'Toolbar Buttons', moduleId: 'SYSTEM_MANAGER' },
          { id: 'sm-set-ftp', label: 'FTP Permissions', moduleId: 'SYSTEM_MANAGER' },
        ]
      },
      { 
        label: 'Operations', 
        items: [
          { id: 'DAY_OPEN', label: 'Day Opening', moduleId: 'DAY_OPEN' },
          { id: 'DAY_CLOSE', label: 'Day Closing', moduleId: 'DAY_CLOSE' },
        ]
      },
      { 
        label: 'Reporting & Database', 
        items: [
          { id: 'sm-rep', label: 'Reports', moduleId: 'SYSTEM_MANAGER' },
          { id: 'sm-db-res', label: 'Restore Database', moduleId: 'SYSTEM_MANAGER' },
          { id: 'sm-db-bak', label: 'Schedule Backup', moduleId: 'SYSTEM_MANAGER' },
        ]
      },
      { 
        label: 'Audit', 
        items: [
          { id: 'sm-aud-log', label: 'User Login Info', moduleId: 'SYSTEM_MANAGER' },
          { id: 'sm-aud-ops', label: 'User Operations', moduleId: 'SYSTEM_MANAGER' },
          { id: 'sm-aud-txn', label: 'Transaction Audit', moduleId: 'SYSTEM_MANAGER' },
        ]
      },
    ]
  },
  {
    id: 'APP_SETUP', 
    label: 'Application Setup', 
    icon: <Briefcase size={16} />,
    description: 'Organization, regional, company, and application settings',
    sections: [
      { 
        label: 'Primary Organization', 
        items: [
          { id: 'as-po-ea', label: 'Enterprise Account', moduleId: 'APP_SETUP' },
          { id: 'as-po-gc', label: 'Group Company', moduleId: 'APP_SETUP' },
          { id: 'as-po-gm', label: 'Group Master', moduleId: 'APP_SETUP' },
          { id: 'as-po-br', label: 'Branch Master', moduleId: 'APP_SETUP' },
          { id: 'as-po-wh', label: 'Warehouse Master', moduleId: 'APP_SETUP' },
        ]
      },
      { 
        label: 'Regional', 
        items: [
          { id: 'as-reg-cnt', label: 'Country', moduleId: 'APP_SETUP' },
          { id: 'as-reg-st', label: 'State', moduleId: 'APP_SETUP' },
          { id: 'as-reg-ct', label: 'City', moduleId: 'APP_SETUP' },
        ]
      },
      { 
        label: 'Company', 
        items: [
          { id: 'as-cmp-bc', label: 'Books Closing', moduleId: 'APP_SETUP' },
          { id: 'as-cmp-yc', label: 'Year Closing', moduleId: 'APP_SETUP' },
        ]
      },
      { 
        label: 'Application Settings', 
        items: [
          { id: 'as-set-pos', label: 'Point of Sale', moduleId: 'APP_SETUP' },
          { id: 'as-set-str', label: 'Store Management', moduleId: 'APP_SETUP' },
          { id: 'as-set-pro', label: 'Procurement', moduleId: 'APP_SETUP' },
          { id: 'as-set-inv', label: 'Inventory', moduleId: 'APP_SETUP' },
          { id: 'as-set-sal', label: 'Sales & Distribution', moduleId: 'APP_SETUP' },
          { id: 'as-set-prd', label: 'Production', moduleId: 'APP_SETUP' },
          { id: 'as-set-mul', label: 'Multi Store Distribution', moduleId: 'APP_SETUP' },
          { id: 'as-set-gen', label: 'General', moduleId: 'APP_SETUP' },
          { id: 'as-set-ql', label: 'Quick Launch', moduleId: 'APP_SETUP' },
          { id: 'as-set-acc', label: 'Accounts Module', moduleId: 'APP_SETUP' },
          { id: 'as-set-pay', label: 'Payroll', moduleId: 'APP_SETUP' },
          { id: 'as-set-log', label: 'Logistic', moduleId: 'APP_SETUP' },
          { id: 'as-set-shp', label: 'Shopping Shop', moduleId: 'APP_SETUP' },
          { id: 'as-set-fra', label: 'Franchises', moduleId: 'APP_SETUP' },
          { id: 'as-set-so', label: 'Sales Order', moduleId: 'APP_SETUP' },
          { id: 'as-set-ds', label: 'Departmental Stores', moduleId: 'APP_SETUP' },
          { id: 'as-set-ye', label: 'Year End', moduleId: 'APP_SETUP' },
          { id: 'as-set-pp', label: 'Product Profitability', moduleId: 'APP_SETUP' },
          { id: 'as-set-sms', label: 'SMS & Mail Configuration', moduleId: 'APP_SETUP' },
        ]
      },
      { 
        label: 'Configurations', 
        items: [
          { id: 'as-cfg-att', label: 'Attribute Setting', moduleId: 'APP_SETUP' },
          { id: 'as-cfg-cl', label: 'Control List Configuration', moduleId: 'APP_SETUP' },
          { id: 'as-cfg-rl', label: 'Report List Configuration', moduleId: 'APP_SETUP' },
          { id: 'as-cfg-rs', label: 'Report Settings', moduleId: 'APP_SETUP' },
          { id: 'as-cfg-sid', label: 'Screen ID Setup', moduleId: 'APP_SETUP' },
          { id: 'as-cfg-bfs', label: 'Bill Format Setup', moduleId: 'APP_SETUP' },
        ]
      },
      { 
        label: 'Barcode & Setup', 
        items: [
          { id: 'as-bar-num', label: 'Barcode Number', moduleId: 'APP_SETUP' },
          { id: 'as-bar-lbl', label: 'Barcode Label Design', moduleId: 'APP_SETUP' },
          { id: 'as-bar-2d', label: '2D Barcode Label Design', moduleId: 'APP_SETUP' },
          { id: 'as-bar-wsc', label: 'Weigh Scale Item Config', moduleId: 'APP_SETUP' },
        ]
      },
      { 
        label: 'Reports', 
        items: [
          { id: 'as-rep-setup', label: 'Report Setup', moduleId: 'APP_SETUP' },
        ]
      },
    ]
  },
  {
    id: 'DATA_MGMT', 
    label: 'Data Management', 
    icon: <FileJson size={16} />,
    description: 'Data import/export and third-party integrations',
    sections: [
      { 
        label: 'Data Import 3P', 
        items: [
          { id: 'dm-imp-ma', label: 'MyAccounts Import', moduleId: 'DATA_MGMT' },
          { id: 'dm-imp-3p', label: '3rd Party Import', moduleId: 'DATA_MGMT' },
        ]
      },
      { 
        label: 'Data Exports', 
        items: [
          { id: 'dm-exp-tal', label: 'Tally Export', moduleId: 'DATA_MGMT' },
        ]
      },
    ]
  },
  {
    id: 'FINAL_ACCOUNTS', 
    label: 'Final Accounts Setup', 
    icon: <BarChart3 size={16} />,
    description: 'Chart of accounts and financial mappings',
    sections: [
      { 
        label: 'Default Accounts', 
        items: [
          { id: 'fa-def-def', label: 'Default', moduleId: 'FINAL_ACCOUNTS' }
        ]
      },
      { 
        label: 'Logistic Accounts', 
        items: [
          { id: 'fa-log-acc', label: 'Accounts', moduleId: 'FINAL_ACCOUNTS' }
        ]
      },
      { 
        label: 'Procurement Accounts', 
        items: [
          { id: 'fa-pro-acc', label: 'Accounts Setup', moduleId: 'FINAL_ACCOUNTS' }
        ]
      },
      { 
        label: 'POS Accounts', 
        items: [
          { id: 'fa-pos-sa', label: 'Sales A/c', moduleId: 'FINAL_ACCOUNTS' },
          { id: 'fa-pos-st', label: 'Sales Tax A/c', moduleId: 'FINAL_ACCOUNTS' }
        ]
      },
      { 
        label: 'HR Accounts', 
        items: [
          { id: 'fa-hr-pay', label: 'Payroll', moduleId: 'FINAL_ACCOUNTS' }
        ]
      },
      { 
        label: 'General Settings', 
        items: [
          { id: 'fa-gen-gen', label: 'General', moduleId: 'FINAL_ACCOUNTS' }
        ]
      },
      { 
        label: 'External Integration', 
        items: [
          { id: 'fa-tal', label: 'Tally', moduleId: 'FINAL_ACCOUNTS' }
        ]
      },
    ]
  },
  {
    id: 'WEB_SETUP', 
    label: 'Web Application Setup', 
    icon: <Globe size={16} />,
    description: 'Web application and server configurations',
    sections: [
      { 
        label: 'MyAccounts Server', 
        items: [
          { id: 'ws-srv-1', label: 'Server I', moduleId: 'WEB_SETUP' }
        ]
      },
    ]
  },
  {
    id: 'SETTINGS_REPORTS', 
    label: 'Settings Reports', 
    icon: <FileText size={16} />,
    description: 'System settings and user reports',
    sections: [
      { 
        label: 'User Reports', 
        items: [
          { id: 'sr-usr-priv', label: 'User Privileges Report', moduleId: 'SETTINGS_REPORTS' }
        ]
      },
    ]
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
   MODULE SELECTOR COMPONENT
   Modern Enterprise UX with Glassmorphism Design
═══════════════════════════════════════════════════════════════════════════ */
const ModuleSelector = ({ onSelect, theme, hubState, onHubStateChange, userRole = '' }) => {
  // Use hubState if provided, otherwise use local state
  const [activeTab, setActiveTab] = useState(hubState?.activeTab || 'apps');
  const [expandedModules, setExpandedModules] = useState(hubState?.expandedModules || {});
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [apps, setApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const isDark = theme === 'dark';
  const { isModuleAllowed, accessUnrestricted } = useLicense();
  // Get DB-driven admin features from ERPContext (set during scoped login)
  const { adminFeatures = [], clientId: ctxClientId = '', role: ctxRole = '' } = useERPContextSafe();
  // "Dynamic Attributes" tab: shown to LICENSOR/SUPER_ADMIN (global) OR
  // ADMINSUPPORT users whose company was granted DYNAMIC_ATTRIBUTES feature.
  const isLicensorAdmin = ['LICENSOR', 'SUPER_ADMIN'].includes(userRole) ||
    (['ADMINSUPPORT', 'LICENSOR'].includes(userRole) && adminFeatures.includes('DYNAMIC_ATTRIBUTES'));
  // "System Config" tab: shown to SUPER_ADMIN, LICENSOR (syscliq*) AND ADMINSUPPORT (ssadmin*)
  // ADMINSUPPORT sees read-only view of their own company's features (can't toggle — that's LICENSOR/SUPER_ADMIN only)
  const isSystemAdmin = ['LICENSOR', 'SUPER_ADMIN', 'ADMINSUPPORT'].includes(userRole);
  // For LICENSOR and ADMINSUPPORT scoped to a company, pre-fill their clientId
  const scopedClientId = (userRole === 'LICENSOR' || userRole === 'ADMINSUPPORT') && ctxClientId ? ctxClientId : '';

  // ── System Config / Feature Manager state ───────────────────────────────
  const [fmClientId, setFmClientId] = useState(scopedClientId);
  const [fmFeatures, setFmFeatures] = useState([]);
  const [fmLoading, setFmLoading] = useState(false);
  const [fmMsg, setFmMsg] = useState('');
  const [fmAllData, setFmAllData] = useState([]);
  const [fmAllLoading, setFmAllLoading] = useState(false);
  const VALID_FEATURE_CODES = ['DYNAMIC_ATTRIBUTES', 'HELP_ENGINE'];
  const FEATURE_META = {
    DYNAMIC_ATTRIBUTES: { label: 'Dynamic Attributes', desc: 'Access to Dynamic Attributes tab (Attribute Studio, Engine Admin, Help Engine screen) in Central Hub', color: 'emerald' },
    HELP_ENGINE: { label: 'Help Engine Editor', desc: 'Ability to write and edit contextual help texts, AI bootstrapper, DevInspector, coverage badge', color: 'indigo' },
  };

  const loadFmFeatures = useCallback(async (cid) => {
    const id = cid || fmClientId;
    if (!id) return;
    setFmLoading(true); setFmMsg('');
    try {
      const res = await api.get(`/api/admin/client-features?clientId=${encodeURIComponent(id)}`);
      if (res.data.success) setFmFeatures(res.data.features || []);
      else setFmMsg(res.data.message || 'Failed to load');
    } catch (e) { setFmMsg(e.message); }
    setFmLoading(false);
  }, [fmClientId]);

  const toggleFmFeature = useCallback(async (featureCode, currentEnabled) => {
    setFmMsg('');
    try {
      const res = await api.post('/api/admin/client-features', {
        clientId: fmClientId, featureCode, isEnabled: !currentEnabled
      });
      if (res.data.success) { setFmMsg(res.data.message); loadFmFeatures(fmClientId); loadFmAll(); }
      else setFmMsg(res.data.message || 'Failed');
    } catch (e) { setFmMsg(e.message); }
  }, [fmClientId, loadFmFeatures]);

  const loadFmAll = useCallback(async () => {
    if (userRole !== 'SUPER_ADMIN') return;
    setFmAllLoading(true);
    try {
      const res = await api.get('/api/admin/client-features/all');
      if (res.data.success) setFmAllData(res.data.data || []);
    } catch (_) {}
    setFmAllLoading(false);
  }, [userRole]);

  // Auto-load for LICENSOR scoped to a company
  useEffect(() => {
    if (isSystemAdmin && scopedClientId) {
      setFmClientId(scopedClientId);
      loadFmFeatures(scopedClientId);
    }
  }, [isSystemAdmin, scopedClientId]);

  // Load all-clients data when System Config tab opens
  useEffect(() => {
    if (activeTab === 'system_config' && userRole === 'SUPER_ADMIN') {
      loadFmAll();
    }
    if (activeTab === 'system_config' && fmClientId) {
      loadFmFeatures(fmClientId);
    }
  }, [activeTab]);

  // Fetch modules from database for Apps tab
  const fetchAppModules = useCallback(async () => {
    setAppsLoading(true);
    try {
      const response = await api.get('/api/menu/modules');
      if (response.data.success && response.data.data?.length > 0) {
        // Filter out setup modules (20-27) and transform to app format
        const apiModules = response.data.data
          .filter(m => !SETUP_MODULE_IDS.includes(m.MODULE_ID))
          .map(m => ({
            id: MODULE_ID_TO_LAUNCH[m.MODULE_ID] || m.MODULE_NAME?.toUpperCase().replace(/\s+/g, '_') || `MODULE_${m.MODULE_ID}`,
            moduleId: m.MODULE_ID,
            label: m.MODULE_NAME,
            icon: getModuleIconByName(m.MODULE_NAME, 20),
            category: getModuleCategory(m.MODULE_ID, m.MODULE_NAME),
            description: `Access ${m.MODULE_NAME} module and its features.`,
          }));
        setApps(apiModules);
      }
    } catch (err) {
      console.error('Error fetching modules for hub:', err);
      // Fallback empty - no mock data
      setApps([]);
    } finally {
      setAppsLoading(false);
    }
  }, []);

  // Sync with hubState when it changes
  useEffect(() => {
    if (hubState) {
      if (hubState.activeTab) setActiveTab(hubState.activeTab);
      if (hubState.expandedModules) setExpandedModules(prev => ({ ...prev, ...hubState.expandedModules }));
      // Auto-expand last visited module
      if (hubState.lastModule) {
        setExpandedModules(prev => ({ ...prev, [hubState.lastModule]: true }));
      }
    }
  }, [hubState]);

  // Fetch apps on mount
  useEffect(() => {
    fetchAppModules();
  }, [fetchAppModules]);

  // Update parent state when local state changes
  const updateActiveTab = (tab) => {
    setActiveTab(tab);
    onHubStateChange?.({ ...hubState, activeTab: tab });
  };

  const updateExpandedModules = (modules) => {
    setExpandedModules(modules);
    onHubStateChange?.({ ...hubState, expandedModules: modules });
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Guard: bounce back to Apps if user can't access tab
  useEffect(() => {
    if (activeTab === 'dynamic_attributes' && !isLicensorAdmin) {
      updateActiveTab('apps');
    }
    if (activeTab === 'system_config' && !isSystemAdmin) {
      updateActiveTab('apps');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isLicensorAdmin, isSystemAdmin]);

  const toggleModule = (id) => {
    const newExpanded = { ...expandedModules, [id]: !expandedModules[id] };
    updateExpandedModules(newExpanded);
  };

  // DIRECT NAVIGATION - No "Open Setup Screen" button required
  const handleItemClick = (item, sectionLabel) => {
    onSelect(item.moduleId, item.id);
    if (isMobile) setShowMobileSidebar(false);
  };

  // Filter modules based on search query
  const filteredSetupModules = SETUP_MODULES.map(module => {
    if (!searchQuery) return module;
    
    const filteredSections = module.sections.map(section => ({
      ...section,
      items: section.items.filter(item => 
        item.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    })).filter(section => section.items.length > 0);

    return { ...module, sections: filteredSections };
  }).filter(module => module.sections.length > 0);

  // Filter apps based on search + license tier privileges
  const filteredApps = apps
    .filter(app => accessUnrestricted || isModuleAllowed(app.moduleId))
    .filter(app =>
      app.label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.category?.toLowerCase().includes(searchQuery.toLowerCase())
    );

  // Get icon for module
  const getModuleIcon = (moduleId) => {
    const iconMap = {
      'MYACCOUNTS_SETUP': <Server size={18} />,
      'SYSTEM_MANAGER': <Settings size={18} />,
      'APP_SETUP': <Briefcase size={18} />,
      'DATA_MGMT': <Database size={18} />,
      'FINAL_ACCOUNTS': <BarChart3 size={18} />,
      'WEB_SETUP': <Globe size={18} />,
      'SETTINGS_REPORTS': <FileText size={18} />,
    };
    return iconMap[moduleId] || <Settings size={18} />;
  };

  return (
    <div data-testid="module-selector" data-dev-component="ModuleSelector" data-dev-file="components/ModuleSelector.js" className={`w-full h-full flex flex-col overflow-hidden relative ${isDark ? 'bg-[#0a0a0a]' : 'bg-gradient-to-br from-slate-50 to-slate-100'}`}>
      {/* Header with Glassmorphism */}
      <div className={`px-4 sm:px-6 py-4 sm:py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b shrink-0 ${
        isDark 
          ? 'border-[#222] bg-gradient-to-r from-[#0e0e10] to-[#151520]' 
          : 'border-slate-200/50 bg-white/80 backdrop-blur-xl'
      }`}>
        <div className="flex items-center gap-3 sm:gap-4">
          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-white shadow-lg ${
            isDark 
              ? 'bg-gradient-to-br from-blue-600 to-blue-700 shadow-blue-500/30' 
              : 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-blue-600/30'
          }`}>
            <Layers size={22} />
          </div>
          <div>
            <h2 className={`text-lg sm:text-xl font-black uppercase tracking-tighter ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Central Hub
            </h2>
            <p className={`text-[9px] sm:text-[10px] font-bold uppercase tracking-widest mt-0.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
              Module Orchestration Center
            </p>
          </div>
        </div>
        
        {/* Tab Switcher with Glass Effect */}
        <div className={`flex p-1 rounded-xl border w-full sm:w-auto ${
          isDark 
            ? 'bg-[#151515]/80 border-[#333] backdrop-blur-sm' 
            : 'bg-white/60 border-slate-200/50 backdrop-blur-sm shadow-inner'
        }`}>
          <button 
            data-testid="hub-apps-tab" 
            onClick={() => updateActiveTab('apps')}
            className={`flex-1 sm:flex-none px-5 sm:px-6 py-2.5 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-2 ${
              activeTab === 'apps' 
                ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/30' 
                : isDark ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
            }`}
          >
            <Monitor size={14} /> Apps
          </button>
          <button 
            data-testid="hub-setup-tab" 
            onClick={() => { updateActiveTab('setup'); if (isMobile) setShowMobileSidebar(true); }}
            className={`flex-1 sm:flex-none px-5 sm:px-6 py-2.5 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-2 ${
              activeTab === 'setup' 
                ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/30' 
                : isDark ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
            }`}
          >
            <Settings size={14} /> Setup
          </button>
          {isLicensorAdmin && (
          <button 
            data-testid="hub-dynamic-attributes-tab" 
            onClick={() => updateActiveTab('dynamic_attributes')}
            className={`flex-1 sm:flex-none px-5 sm:px-6 py-2.5 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-2 ${
              activeTab === 'dynamic_attributes' 
                ? 'bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-lg shadow-emerald-500/30' 
                : isDark ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
            }`}
          >
            <span style={{ fontSize: 12 }}>⚙</span> Dynamic Attributes
          </button>
          )}
          {isSystemAdmin && (
          <button
            data-testid="hub-system-config-tab"
            onClick={() => updateActiveTab('system_config')}
            className={`flex-1 sm:flex-none px-5 sm:px-6 py-2.5 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-2 ${
              activeTab === 'system_config'
                ? 'bg-gradient-to-r from-violet-600 to-violet-500 text-white shadow-lg shadow-violet-500/30'
                : isDark ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
            }`}
          >
            <Shield size={13} /> System Config
          </button>
          )}
          {userRole === 'SUPER_ADMIN' && (
          <button
            data-testid="hub-tenant-hub-tab"
            onClick={() => updateActiveTab('tenant_hub')}
            className={`flex-1 sm:flex-none px-5 sm:px-6 py-2.5 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-2 ${
              activeTab === 'tenant_hub'
                ? 'bg-gradient-to-r from-blue-700 to-blue-600 text-white shadow-lg shadow-blue-600/30'
                : isDark ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
            }`}
          >
            <Building2 size={13} /> Tenant Hub
          </button>
          )}
          {/* Industry POS tab — always visible for testing */}
          <button
            data-testid="hub-industry-pos-tab"
            onClick={() => updateActiveTab('industry_pos')}
            className={`flex-1 sm:flex-none px-4 sm:px-5 py-2.5 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-2 ${
              activeTab === 'industry_pos'
                ? 'bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-lg shadow-orange-500/30'
                : isDark ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
            }`}
          >
            <Store size={13} /> Industry POS
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {activeTab === 'apps' ? (
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {appsLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center space-y-4">
                  <Loader2 size={32} className="mx-auto text-blue-500 animate-spin" />
                  <p className={`font-bold ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Loading modules from database...</p>
                </div>
              </div>
            ) : filteredApps.length === 0 ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center space-y-4">
                  <Database size={32} className={`mx-auto ${isDark ? 'text-slate-600' : 'text-slate-400'}`} />
                  <p className={`font-bold ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    {searchQuery ? 'No modules match your search' : 'No modules available'}
                  </p>
                  <button onClick={fetchAppModules} className="text-blue-500 underline text-sm">Refresh</button>
                </div>
              </div>
            ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {filteredApps.map((module) => (
                <button 
                  key={module.id} 
                  data-testid={`hub-module-${String(module.id).toLowerCase()}`} 
                  onClick={() => onSelect(module.id)}
                  className={`group relative p-4 sm:p-5 rounded-xl sm:rounded-2xl border transition-all text-left flex flex-col gap-3 sm:gap-4 active:scale-[0.98] overflow-hidden ${
                    isDark 
                      ? 'bg-gradient-to-br from-[#151515] to-[#1a1a1a] border-[#2a2a2a] hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/10' 
                      : 'bg-white/80 backdrop-blur-sm border-slate-200/50 hover:border-blue-400 shadow-sm hover:shadow-xl hover:shadow-blue-500/10'
                  }`}
                >
                  {/* Gradient overlay on hover */}
                  <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity ${
                    isDark ? 'bg-gradient-to-br from-blue-600/5 to-transparent' : 'bg-gradient-to-br from-blue-50/50 to-transparent'
                  }`} />
                  
                  <div className={`relative w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center transition-all ${
                    isDark 
                      ? 'bg-gradient-to-br from-[#1a1a1a] to-[#222] text-blue-400 group-hover:from-blue-600 group-hover:to-blue-500 group-hover:text-white group-hover:shadow-lg group-hover:shadow-blue-500/30' 
                      : 'bg-gradient-to-br from-slate-50 to-slate-100 text-blue-600 group-hover:from-blue-600 group-hover:to-blue-500 group-hover:text-white group-hover:shadow-lg group-hover:shadow-blue-500/30'
                  }`}>
                    {module.icon}
                  </div>
                  <div className="relative">
                    <h3 className={`text-sm font-black uppercase tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {module.label}
                    </h3>
                    <p className={`text-[10px] mt-1 leading-relaxed line-clamp-2 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                      {module.description || 'Module ready.'}
                    </p>
                  </div>
                  <div className="relative mt-auto flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-[9px] font-black text-blue-500 uppercase tracking-widest">Launch</span>
                    <ArrowRight size={12} className="text-blue-500" />
                  </div>
                </button>
              ))}
            </div>
            )}
          </div>
        ) : activeTab === 'dynamic_attributes' && isLicensorAdmin ? (
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className={`mb-6 p-5 rounded-2xl border ${isDark ? 'bg-[#151515] border-[#2a2a2a]' : 'bg-white border-slate-200 shadow-sm'}`}>
              <h2 className={`text-lg font-black tracking-tight mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Dynamic Attribute Engine
              </h2>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Configure industry-specific product attributes, form layouts, and solutions.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { id: 'ATTRIBUTE_STUDIO', label: 'Attribute & Pricing Studio', desc: 'Real-DB attribute + pricing config — maps industries to real ItemMaster/Price_Master columns, multi-tenant aware', icon: '🧬', color: 'emerald' },
                { id: 'ITEM_CREATION_NEW', label: 'Item Master', desc: 'Manage all product master records — classification, pricing, compliance and industry-specific attribute values', icon: '📦', color: 'emerald' },
                { id: 'ITEM_LIST', label: 'Item List', desc: 'Browse, search and manage all saved items with dynamic filters by division, category, brand and status', icon: '📋', color: 'blue' },
                { id: 'DYNAMIC_ATTRIBUTES', label: 'Attribute Engine Admin', desc: 'Manage attributes, layouts, solutions, and preview forms', icon: '⚙', color: 'emerald' },
                { id: 'HELP_ENGINE', label: 'Help Engine', desc: 'AI-powered contextual help text, tooltip management & Dev Inspector', icon: '📖', color: 'indigo' },
              ].map(item => (
                <button key={item.id} data-testid={`hub-${item.id.toLowerCase()}`} onClick={() => onSelect(item.id)}
                  className={`group relative p-5 rounded-2xl border transition-all text-left flex flex-col gap-4 active:scale-[0.98] overflow-hidden ${isDark ? 'bg-gradient-to-br from-[#151515] to-[#1a1a1a] border-[#2a2a2a] hover:border-emerald-500/50 hover:shadow-lg hover:shadow-emerald-500/10' : 'bg-white/80 backdrop-blur-sm border-slate-200/50 hover:border-emerald-400 shadow-sm hover:shadow-xl hover:shadow-emerald-500/10'}`}>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-all ${isDark ? 'bg-gradient-to-br from-[#1a1a1a] to-[#222] group-hover:from-emerald-600 group-hover:to-emerald-500 group-hover:shadow-lg' : 'bg-gradient-to-br from-slate-50 to-slate-100 group-hover:from-emerald-600 group-hover:to-emerald-500 group-hover:shadow-lg'}`}>
                    {item.icon}
                  </div>
                  <div>
                    <h3 className={`text-sm font-black uppercase tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.label}</h3>
                    <p className={`text-[10px] mt-1 leading-relaxed ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{item.desc}</p>
                  </div>
                  <div className="mt-auto flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-[9px] font-black text-emerald-500 uppercase tracking-widest">Launch</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                  </div>
                </button>
              ))}
            </div>

          </div>
        ) : activeTab === 'tenant_hub' && userRole === 'SUPER_ADMIN' ? (
          <TenantHubTab isDark={isDark} />
        ) : activeTab === 'industry_pos' ? (
          /* ── Industry POS Tab — quick launch for POS billing screens only ──
             NOTE: Purchase Invoice / GRN screens are in the Procurement
             sidebar (dynamic from DB). Access them via Procurement → GRN &
             Purchase Invoice in the side navigation.                         */
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
            <div className={`p-4 rounded-xl border ${isDark ? 'bg-amber-500/5 border-amber-500/20' : 'bg-amber-50 border-amber-200'}`}>
              <p className={`text-xs font-bold ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                📌 Purchase Invoice, GRN and other procurement screens are available in the <b>Procurement → GRN &amp; Purchase Invoice</b> sidebar section (loaded dynamically from the database).
              </p>
            </div>
            {/* POS Billing Screens */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-orange-500/15 flex items-center justify-center text-orange-500 text-base">🏪</div>
                <div>
                  <h2 className={`text-sm font-black uppercase tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>Industry POS Billing</h2>
                  <p className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Point-of-sale screens for different retail formats — also in POS System sidebar</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { id:'APPAREL_POS',     label:'Apparel / Retail POS',    desc:'Fashion & clothing retail — size/colour variants, barcode scan, loyalty',  icon:'👔', color:'blue',   screen:'6.16.11' },
                  { id:'SUPERMARKET_POS', label:'Supermarket POS',          desc:'Fast checkout — direct item add, weight-based pricing, barcode scan',      icon:'🛒', color:'green',  screen:'6.16.12' },
                  { id:'FRESH_VEG_POS',   label:'Fresh / Veg Counter POS',  desc:'Weighing scale integration — kg/gram pricing, multi-unit rates',          icon:'🥦', color:'emerald',screen:'6.16.13' },
                  { id:'FOOD_COURT_POS',  label:'Food Court / Restaurant',  desc:'Dine In/Take Away, table map, KOT printing, service workflow',             icon:'🍽', color:'orange', screen:'6.16.14' },
                ].map(item => {
                  const colorMap = {
                    blue:   { bg: isDark ? 'from-blue-600/5   to-blue-500/5'   : 'from-blue-50   to-sky-50',     border: isDark ? 'hover:border-blue-500/40'    : 'hover:border-blue-400',    icon: isDark ? 'from-blue-700   to-blue-600'   : 'from-blue-500   to-blue-600',    badge: isDark ? 'bg-blue-500/15   text-blue-400'   : 'bg-blue-100   text-blue-700',   launch: 'text-blue-500',   arrow: 'text-blue-500'   },
                    green:  { bg: isDark ? 'from-green-600/5  to-green-500/5'  : 'from-green-50  to-emerald-50', border: isDark ? 'hover:border-green-500/40'   : 'hover:border-green-400',   icon: isDark ? 'from-green-700  to-green-600'  : 'from-green-500  to-green-600',   badge: isDark ? 'bg-green-500/15  text-green-400'  : 'bg-green-100  text-green-700',  launch: 'text-green-500',  arrow: 'text-green-500'  },
                    emerald:{ bg: isDark ? 'from-emerald-600/5 to-emerald-500/5': 'from-emerald-50 to-teal-50',  border: isDark ? 'hover:border-emerald-500/40' : 'hover:border-emerald-400', icon: isDark ? 'from-emerald-700 to-emerald-600': 'from-emerald-500 to-emerald-600',badge: isDark ? 'bg-emerald-500/15 text-emerald-400': 'bg-emerald-100 text-emerald-700',launch:'text-emerald-500', arrow:'text-emerald-500'},
                    orange: { bg: isDark ? 'from-orange-600/5  to-orange-500/5' : 'from-orange-50  to-amber-50',  border: isDark ? 'hover:border-orange-500/40'  : 'hover:border-orange-400',  icon: isDark ? 'from-orange-700  to-orange-600' : 'from-orange-500  to-orange-600', badge: isDark ? 'bg-orange-500/15  text-orange-400' : 'bg-orange-100  text-orange-700', launch:'text-orange-500', arrow:'text-orange-500'},
                  };
                  const c = colorMap[item.color];
                  return (
                    <button key={item.id} data-testid={`hub-${item.id.toLowerCase()}`}
                      onClick={() => onSelect(item.id)}
                      className={`group relative p-4 sm:p-5 rounded-2xl border transition-all text-left flex flex-col gap-3 active:scale-[0.98] overflow-hidden ${isDark ? `bg-gradient-to-br ${c.bg} border-[#2a2a2a] ${c.border} hover:shadow-lg` : `bg-gradient-to-br ${c.bg} border-slate-200/50 ${c.border} shadow-sm hover:shadow-xl`}`}>
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-all bg-gradient-to-br ${isDark ? 'from-[#1a1a1a] to-[#222]' : 'from-white to-slate-100'} group-hover:bg-gradient-to-br group-hover:${c.icon} group-hover:shadow-lg`}>
                        {item.icon}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={`text-xs font-black uppercase tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.label}</h3>
                          <span className={`text-[8px] font-black px-1.5 py-0.5 rounded ${c.badge}`}>{item.screen}</span>
                        </div>
                        <p className={`text-[10px] leading-relaxed ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{item.desc}</p>
                      </div>
                      <div className={`mt-auto flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity`}>
                        <span className={`text-[9px] font-black uppercase tracking-widest ${c.launch}`}>Launch</span>
                        <ArrowRight size={11} className={c.arrow} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : activeTab === 'system_config' && isSystemAdmin ? (
          /* ── System Config Tab — Client Admin Feature Manager ── */
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
            {/* Header */}
            <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[#151515] border-[#2a2a2a]' : 'bg-white border-slate-200 shadow-sm'}`}>
              <div className="flex items-center gap-3 mb-1">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-violet-600/20 text-violet-400' : 'bg-violet-100 text-violet-600'}`}>
                  <Shield size={20} />
                </div>
                <div>
                  <h2 className={`text-base font-black tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Client Admin Feature Manager
                  </h2>
                  <p className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    Grant or revoke super-admin features for each company's admin users. Scoped — admins only see their own company.
                  </p>
                </div>
              </div>
            </div>

            {/* ── SUPER_ADMIN: "Your Account" global-always-on section ── */}
            {userRole === 'SUPER_ADMIN' && (
              <div className={`p-5 rounded-2xl border ${isDark ? 'bg-gradient-to-br from-violet-900/20 to-violet-800/10 border-violet-500/30' : 'bg-gradient-to-br from-violet-50 to-indigo-50 border-violet-200'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Zap size={14} className={isDark ? 'text-violet-400' : 'text-violet-600'} />
                  <p className={`text-[9px] font-black uppercase tracking-wider ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>Your Account — Global System Admin</p>
                  <span className={`ml-auto text-[8px] font-black px-2 py-0.5 rounded-full ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>ALL FEATURES ON</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {VALID_FEATURE_CODES.map(code => {
                    const meta = FEATURE_META[code] || { label: code, desc: '' };
                    return (
                      <div key={`global-${code}`} className={`p-4 rounded-xl border ${isDark ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div>
                            <p className={`text-xs font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>{meta.label}</p>
                            <p className={`text-[9px] font-mono ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{code}</p>
                          </div>
                          <span className="flex items-center gap-1 text-[9px] font-black px-2 py-0.5 rounded-full bg-emerald-500 text-white shrink-0">
                            <ToggleRight size={11} /> ON
                          </span>
                        </div>
                        <p className={`text-[9px] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{meta.desc}</p>
                        <p className={`text-[8px] mt-1 ${isDark ? 'text-violet-400/60' : 'text-violet-400'}`}>Globally enabled — SUPER_ADMIN has all access by default</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Feature Manager Panel */}
            <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[#151515] border-[#2a2a2a]' : 'bg-white border-slate-200 shadow-sm'}`}>
              <p className={`text-[9px] font-black uppercase tracking-wider mb-3 ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>
                {userRole === 'SUPER_ADMIN' ? 'Manage Features for a Company' : userRole === 'ADMINSUPPORT' ? 'Your Enabled Features' : 'Your Company Features'}
              </p>

              {/* Client ID input — SUPER_ADMIN types one; LICENSOR/ADMINSUPPORT see their own company */}
              {userRole === 'SUPER_ADMIN' ? (
                <div className="flex gap-2 mb-4">
                  <input
                    data-testid="sysconfig-client-id-input"
                    value={fmClientId}
                    onChange={e => setFmClientId(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && loadFmFeatures(fmClientId)}
                    placeholder="Enter Client ID (e.g. IN_EXP_0002)"
                    className={`flex-1 text-xs px-3 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-violet-500 ${isDark ? 'bg-[#1a1a1a] border-[#333] text-white placeholder:text-slate-600' : 'bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400'}`}
                  />
                  <button
                    data-testid="sysconfig-load-btn"
                    onClick={() => loadFmFeatures(fmClientId)}
                    disabled={fmLoading || !fmClientId.trim()}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition-all ${fmLoading || !fmClientId.trim() ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90 active:scale-95'} bg-violet-600 text-white`}
                  >
                    {fmLoading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
                    Load
                  </button>
                </div>
              ) : (
                <div className={`flex items-center gap-2 mb-4 px-3 py-2 rounded-lg border ${isDark ? 'bg-[#1a1a1a] border-[#333]' : 'bg-slate-50 border-slate-200'}`}>
                  <Building2 size={14} className={isDark ? 'text-violet-400' : 'text-violet-600'} />
                  <span className={`text-xs font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{fmClientId || ctxClientId}</span>
                  <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-bold ${isDark ? 'bg-violet-600/20 text-violet-400' : 'bg-violet-100 text-violet-700'}`}>
                    {userRole === 'ADMINSUPPORT' ? 'Your Company (read-only)' : 'Your Company'}
                  </span>
                </div>
              )}

              {/* Feature Toggles */}
              {fmFeatures.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {VALID_FEATURE_CODES.map(code => {
                    const row = fmFeatures.find(f => f.FeatureCode === code);
                    const enabled = row ? !!row.IsEnabled : false;
                    const meta = FEATURE_META[code] || { label: code, desc: '', color: 'slate' };
                    return (
                      <div key={code}
                        className={`p-4 rounded-xl border transition-all ${
                          enabled
                            ? isDark ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-emerald-50 border-emerald-300'
                            : isDark ? 'bg-[#111] border-[#2a2a2a]' : 'bg-slate-50 border-slate-200'
                        }`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <p className={`text-xs font-black uppercase tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>{meta.label}</p>
                            <p className={`text-[9px] font-mono mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{code}</p>
                          </div>
                          <button
                            data-testid={`sysconfig-toggle-${code}`}
                            onClick={() => userRole !== 'ADMINSUPPORT' && toggleFmFeature(code, enabled)}
                            disabled={userRole === 'ADMINSUPPORT'}
                            title={userRole === 'ADMINSUPPORT' ? 'Contact your LICENSOR to change features' : ''}
                            className={`shrink-0 flex items-center gap-1 px-3 py-1 rounded-full text-[9px] font-black uppercase transition-all ${
                              userRole === 'ADMINSUPPORT'
                                ? 'opacity-70 cursor-not-allowed ' + (enabled ? 'bg-emerald-500 text-white' : (isDark ? 'bg-[#2a2a2a] text-slate-400' : 'bg-slate-200 text-slate-600'))
                                : 'active:scale-95 ' + (enabled ? 'bg-emerald-500 text-white shadow-emerald-500/30 shadow-md' : (isDark ? 'bg-[#2a2a2a] text-slate-400 hover:bg-[#333]' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'))
                            }`}
                          >
                            {enabled ? <ToggleRight size={12} /> : <ToggleLeft size={12} />}
                            {enabled ? 'ON' : 'OFF'}
                          </button>
                        </div>
                        <p className={`text-[9px] leading-relaxed ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{meta.desc}</p>
                        {row?.GrantedBy && (
                          <p className={`text-[8px] mt-2 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                            Managed by: {row.GrantedBy}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : fmClientId && !fmLoading ? (
                <div className={`text-center py-6 rounded-xl border ${isDark ? 'border-[#2a2a2a] text-slate-500' : 'border-slate-200 text-slate-400'}`}>
                  <Shield size={24} className="mx-auto mb-2 opacity-30" />
                  <p className="text-xs font-medium">No features found. Enter a valid Client ID and click Load.</p>
                </div>
              ) : !fmClientId ? (
                <div className={`text-center py-6 rounded-xl border ${isDark ? 'border-[#2a2a2a] text-slate-600' : 'border-dashed border-slate-300 text-slate-400'}`}>
                  <Search size={24} className="mx-auto mb-2 opacity-30" />
                  <p className="text-xs">Enter a Client ID above to manage their admin features.</p>
                </div>
              ) : null}

              {fmMsg && (
                <p data-testid="sysconfig-msg" className={`text-xs mt-3 font-semibold px-3 py-2 rounded-lg ${
                  fmMsg.toLowerCase().startsWith('fail') || fmMsg.toLowerCase().startsWith('err') || fmMsg.toLowerCase().startsWith('invalid')
                    ? isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600'
                    : isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-700'
                }`}>
                  {fmMsg}
                </p>
              )}
            </div>

            {/* All Clients Overview — SUPER_ADMIN only */}
            {userRole === 'SUPER_ADMIN' && (
              <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[#151515] border-[#2a2a2a]' : 'bg-white border-slate-200 shadow-sm'}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className={`text-[9px] font-black uppercase tracking-wider ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>
                    All Registered Companies
                  </p>
                  <button onClick={loadFmAll} disabled={fmAllLoading}
                    className={`flex items-center gap-1 text-[9px] font-bold px-2 py-1 rounded-lg transition-colors ${isDark ? 'text-slate-500 hover:text-white hover:bg-white/5' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'}`}>
                    {fmAllLoading ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                    Refresh
                  </button>
                </div>
                {fmAllData.length === 0 && !fmAllLoading ? (
                  <p className={`text-xs ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>No feature grants found yet. Companies auto-seed on first admin login.</p>
                ) : (
                  <div className="space-y-1.5">
                    {/* Group by ClientId */}
                    {Object.entries(
                      fmAllData.reduce((acc, r) => {
                        if (!acc[r.ClientId]) acc[r.ClientId] = { name: r.CompName || r.ClientId, features: [] };
                        acc[r.ClientId].features.push(r);
                        return acc;
                      }, {})
                    ).map(([cid, info]) => (
                      <div key={cid}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${
                          fmClientId === cid
                            ? isDark ? 'bg-violet-600/10 border-violet-500/30' : 'bg-violet-50 border-violet-300'
                            : isDark ? 'bg-[#111] border-[#2a2a2a] hover:border-[#3a3a3a]' : 'bg-slate-50 border-slate-100 hover:border-slate-300'
                        }`}
                        onClick={() => { setFmClientId(cid); loadFmFeatures(cid); }}>
                        <Building2 size={13} className={isDark ? 'text-violet-400 shrink-0' : 'text-violet-600 shrink-0'} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-[10px] font-bold truncate ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{info.name}</p>
                          <p className={`text-[9px] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>{cid}</p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {info.features.map(f => (
                            <span key={f.FeatureCode} className={`text-[7px] font-black px-1.5 py-0.5 rounded uppercase ${
                              f.IsEnabled
                                ? isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
                                : isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'
                            }`}>
                              {f.FeatureCode === 'DYNAMIC_ATTRIBUTES' ? 'DYN' : 'HELP'}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden relative">
            {/* Mobile sidebar backdrop */}
            {isMobile && showMobileSidebar && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={() => setShowMobileSidebar(false)} />
            )}

            {/* Setup sidebar with all modules */}
            <div className={`${
              isMobile 
                ? `fixed left-0 top-0 h-full z-50 transition-transform duration-300 ${showMobileSidebar ? 'translate-x-0' : '-translate-x-full'} w-80`
                : 'flex-shrink-0 w-72 lg:w-80'
            } border-r overflow-hidden flex flex-col ${
              isDark 
                ? 'bg-gradient-to-b from-[#0e0e10] to-[#0a0a0a] border-[#222]' 
                : 'bg-gradient-to-b from-white to-slate-50/80 border-slate-200/50'
            }`}>
              
              {/* Search Bar */}
              <div className={`px-4 py-3 border-b shrink-0 ${isDark ? 'border-[#222]' : 'border-slate-200/50'}`}>
                <div className={`relative flex items-center ${
                  isDark ? 'bg-[#151515] border-[#333]' : 'bg-slate-100/80 border-slate-200/50'
                } border rounded-lg`}>
                  <Search size={14} className={`absolute left-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                  <input
                    type="text"
                    placeholder="Search screens..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`w-full pl-9 pr-4 py-2 text-xs bg-transparent outline-none ${
                      isDark ? 'text-white placeholder:text-slate-600' : 'text-slate-900 placeholder:text-slate-400'
                    }`}
                  />
                </div>
              </div>

              {/* Mobile close button */}
              {isMobile && (
                <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-[#222]' : 'border-slate-200'}`}>
                  <span className={`text-xs font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Setup Modules</span>
                  <button onClick={() => setShowMobileSidebar(false)} className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-200'}`}>
                    <X size={18} />
                  </button>
                </div>
              )}

              {!isMobile && (
                <div className={`px-4 py-3 border-b sticky top-0 z-10 ${
                  isDark ? 'bg-[#0e0e10] border-[#222]' : 'bg-white/90 backdrop-blur-sm border-slate-200/50'
                }`}>
                  <p className={`text-[9px] font-black uppercase tracking-[0.2em] ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                    Setup Modules ({filteredSetupModules.length})
                  </p>
                </div>
              )}

              {/* Modules List */}
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {filteredSetupModules.map((module) => (
                  <div key={module.id} className="mb-1">
                    <button 
                      onClick={() => toggleModule(module.id)} 
                      data-testid={`setup-module-${module.id.toLowerCase()}`}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-all ${
                        expandedModules[module.id] 
                          ? isDark 
                            ? 'bg-gradient-to-r from-blue-600/20 to-blue-500/10 text-blue-400 shadow-inner' 
                            : 'bg-gradient-to-r from-blue-50 to-blue-100/50 text-blue-700'
                          : isDark 
                            ? 'text-slate-400 hover:text-white hover:bg-white/5' 
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/80'
                      }`}
                    >
                      <span className={expandedModules[module.id] ? (isDark ? 'text-blue-400' : 'text-blue-600') : ''}>
                        {module.icon}
                      </span>
                      <span className="text-[11px] font-bold uppercase tracking-tight flex-1 text-left">
                        {module.label}
                      </span>
                      <ChevronRight size={14} className={`transition-transform duration-200 ${expandedModules[module.id] ? 'rotate-90' : ''}`} />
                    </button>
                    
                    {/* Expanded Sections with DIRECT NAVIGATION */}
                    {expandedModules[module.id] && (
                      <div className={`ml-3 my-1 border-l-2 ${isDark ? 'border-blue-500/30' : 'border-blue-200'}`}>
                        {module.sections.map((section, sIdx) => (
                          <div key={sIdx} className="mb-2">
                            <p className={`px-4 py-1.5 text-[9px] font-black uppercase tracking-wider ${
                              isDark ? 'text-slate-500' : 'text-slate-400'
                            }`}>
                              {section.label}
                            </p>
                            {section.items.map((item) => (
                              <button 
                                key={item.id} 
                                data-testid={`setup-sub-${item.id}`} 
                                onClick={() => handleItemClick(item, section.label)}
                                onMouseEnter={() => setHoveredItem(item.id)}
                                onMouseLeave={() => setHoveredItem(null)}
                                className={`w-full text-left px-4 py-2 text-[11px] font-medium transition-all rounded-r-lg group flex items-center justify-between ${
                                  hoveredItem === item.id
                                    ? isDark 
                                      ? 'bg-blue-600/20 text-blue-400 border-l-2 border-blue-500 -ml-[2px]' 
                                      : 'bg-blue-50 text-blue-700 border-l-2 border-blue-500 -ml-[2px]'
                                    : isDark 
                                      ? 'text-slate-500 hover:text-white hover:bg-white/5' 
                                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                                }`}
                              >
                                <span>{item.label}</span>
                                <ArrowRight size={12} className={`opacity-0 group-hover:opacity-100 transition-opacity ${
                                  isDark ? 'text-blue-400' : 'text-blue-600'
                                }`} />
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile menu toggle */}
            {isMobile && !showMobileSidebar && (
              <button 
                onClick={() => setShowMobileSidebar(true)}
                className={`absolute left-4 top-4 z-30 p-3 rounded-xl border shadow-lg ${
                  isDark 
                    ? 'bg-[#1a1a1a] border-[#333] text-white' 
                    : 'bg-white border-slate-200 text-slate-700'
                }`}
              >
                <Menu size={20} />
              </button>
            )}

            {/* Setup Info Panel - Shows when no item selected */}
            <div className={`flex-1 overflow-y-auto p-4 sm:p-8 ${isDark ? 'bg-[#0a0a0a]' : 'bg-gradient-to-br from-slate-50 to-slate-100'}`}>
              <div className="max-w-2xl mx-auto">
                {/* Welcome Card */}
                <div className={`p-6 sm:p-8 rounded-2xl border mb-6 ${
                  isDark 
                    ? 'bg-gradient-to-br from-[#151515] to-[#1a1a20] border-[#2a2a2a]' 
                    : 'bg-white/80 backdrop-blur-sm border-slate-200/50 shadow-xl shadow-slate-200/50'
                }`}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                      isDark 
                        ? 'bg-gradient-to-br from-blue-600/20 to-blue-500/10 text-blue-400' 
                        : 'bg-gradient-to-br from-blue-50 to-blue-100 text-blue-600'
                    }`}>
                      <Settings size={28} />
                    </div>
                    <div>
                      <h2 className={`text-xl font-black uppercase tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Setup Center
                      </h2>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                        Configure all ERP modules and screens
                      </p>
                    </div>
                  </div>
                  
                  <p className={`text-sm leading-relaxed mb-6 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    Select any screen from the sidebar to <span className="font-bold text-blue-500">instantly open</span> it. 
                    All {SETUP_MODULES.reduce((acc, m) => acc + m.sections.reduce((a, s) => a + s.items.length, 0), 0)} setup screens are available for direct access.
                  </p>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {SETUP_MODULES.slice(0, 4).map((module) => (
                      <div 
                        key={module.id}
                        onClick={() => toggleModule(module.id)}
                        className={`p-3 rounded-xl border cursor-pointer transition-all ${
                          isDark 
                            ? 'bg-[#111] border-[#2a2a2a] hover:border-blue-500/50' 
                            : 'bg-slate-50/80 border-slate-200/50 hover:border-blue-400'
                        }`}
                      >
                        <div className={`text-lg font-black ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                          {module.sections.reduce((acc, s) => acc + s.items.length, 0)}
                        </div>
                        <div className={`text-[9px] font-bold uppercase tracking-wide ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                          {module.label.split(' ')[0]}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Instructions */}
                <div className={`p-4 rounded-xl border ${
                  isDark ? 'bg-blue-500/5 border-blue-500/20' : 'bg-blue-50/50 border-blue-200/50'
                }`}>
                  <p className={`text-xs font-medium flex items-center gap-2 ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                    <Circle size={8} className="fill-current" />
                    Click any screen in the sidebar to open it directly - no extra clicks needed
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Floating System Config Button (SUPER_ADMIN / LICENSOR only) ── */}
      {isSystemAdmin && activeTab !== 'system_config' && (
        <button
          data-testid="hub-sysconfig-fab"
          onClick={() => updateActiveTab('system_config')}
          title="System Configuration — Client Admin Features"
          className={`absolute bottom-14 right-4 z-20 flex items-center gap-2 px-3 py-2 rounded-full text-[9px] font-black uppercase tracking-wider shadow-lg transition-all hover:scale-105 active:scale-95 ${
            isDark
              ? 'bg-gradient-to-r from-violet-600 to-violet-500 text-white shadow-violet-500/40'
              : 'bg-gradient-to-r from-violet-600 to-violet-500 text-white shadow-violet-600/30'
          }`}
        >
          <Shield size={11} />
          Sys Config
        </button>
      )}

      {/* Footer */}
      <div className={`px-4 sm:px-6 py-2 sm:py-3 border-t flex justify-between items-center shrink-0 ${
        isDark 
          ? 'bg-[#0e0e10] border-[#222]' 
          : 'bg-white/80 backdrop-blur-sm border-slate-200/50'
      }`}>
        <div className={`flex items-center gap-3 sm:gap-4 text-[8px] sm:text-[9px] font-bold uppercase tracking-widest ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
          <span className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 animate-pulse" /> 
            System Stable
          </span>
          <span className="hidden sm:flex items-center gap-1.5">
            <Activity size={10} /> All Modules Active
          </span>
        </div>
        <p className={`text-[8px] sm:text-[9px] font-mono ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
          RetailCLIQ ERP v2.7
        </p>
      </div>
    </div>
  );
};

export default ModuleSelector;
